/* CarGear – theme.js (no dependencies) */
(() => {
  'use strict';

  const theme = window.theme || {};
  const $ = (sel, root = document) => root.querySelector(sel);
  const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));
  const overlay = () => $('[data-overlay]');

  /* ---------- Money ---------- */
  function formatMoney(cents, format = theme.moneyFormat || '€{{amount}}') {
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const value = Number(cents) / 100;
    const fmt = (decimals, thousands, decimal) => {
      const fixed = value.toFixed(decimals).split('.');
      fixed[0] = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
      return fixed.join(decimal);
    };
    return format.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
      switch (key) {
        case 'amount_no_decimals': return fmt(0, ',', '.');
        case 'amount_with_comma_separator': return fmt(2, '.', ',');
        case 'amount_no_decimals_with_comma_separator': return fmt(0, '.', ',');
        case 'amount_with_apostrophe_separator': return fmt(2, "'", '.');
        default: return fmt(2, ',', '.');
      }
    });
  }

  /* ---------- Drawers ---------- */
  let activeDrawer = null;
  let lastFocus = null;

  function openDrawer(id) {
    const drawer = document.getElementById(id);
    if (!drawer) return;
    if (activeDrawer && activeDrawer !== drawer) closeDrawer(false);
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    overlay()?.classList.add('is-visible');
    document.body.classList.add('is-locked');
    $$(`[aria-controls="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'true'));
    activeDrawer = drawer;
    setTimeout(() => drawer.focus({ preventScroll: true }), 50);
  }

  function closeDrawer(restoreFocus = true) {
    if (!activeDrawer) return;
    const id = activeDrawer.id;
    activeDrawer.classList.remove('is-open');
    activeDrawer.setAttribute('aria-hidden', 'true');
    $$(`[aria-controls="${id}"]`).forEach((b) => b.setAttribute('aria-expanded', 'false'));
    activeDrawer = null;
    overlay()?.classList.remove('is-visible');
    document.body.classList.remove('is-locked');
    if (restoreFocus && lastFocus) lastFocus.focus({ preventScroll: true });
  }

  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      openDrawer(opener.dataset.drawerOpen);
      return;
    }
    if (e.target.closest('[data-drawer-close]') || e.target.matches('[data-overlay]')) {
      closeDrawer();
      closeSearch();
    }
    const cartToggle = e.target.closest('[data-cart-toggle]');
    if (cartToggle && theme.cartType === 'drawer' && document.getElementById('CartDrawer')) {
      e.preventDefault();
      openDrawer('CartDrawer');
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      closeDrawer();
      closeSearch();
    }
  });

  /* ---------- Header ---------- */
  function initHeader() {
    const header = $('[data-header]');
    if (!header) return;
    const setHeight = () => document.documentElement.style.setProperty('--header-height', `${header.offsetHeight}px`);
    setHeight();
    window.addEventListener('resize', setHeight, { passive: true });

    if (header.classList.contains('header-wrapper--transparent')) {
      const update = () => header.classList.toggle('is-solid', window.scrollY > 10 || header.matches(':hover, :focus-within'));
      update();
      window.addEventListener('scroll', update, { passive: true });
      header.addEventListener('mouseenter', update);
      header.addEventListener('mouseleave', update);
      header.addEventListener('focusin', update);
      header.addEventListener('focusout', () => setTimeout(update, 0));
    }
  }

  /* ---------- Announcement bar ---------- */
  function initAnnouncements() {
    $$('[data-announcement]').forEach((track) => {
      const items = $$('.announcement__item', track);
      if (items.length < 2 || track.dataset.ready) return;
      track.dataset.ready = '1';
      let i = 0;
      setInterval(() => {
        if (document.hidden) return;
        const current = items[i];
        i = (i + 1) % items.length;
        current.classList.remove('is-active');
        current.classList.add('is-leaving');
        items[i].classList.remove('is-leaving');
        items[i].classList.add('is-active');
        setTimeout(() => current.classList.remove('is-leaving'), 600);
      }, Number(track.dataset.interval) || 5000);
    });
  }

  /* ---------- Search ---------- */
  const searchModal = () => document.getElementById('SearchModal');

  function openSearch() {
    const modal = searchModal();
    if (!modal) return;
    closeDrawer(false);
    modal.hidden = false;
    overlay()?.classList.add('is-visible');
    document.body.classList.add('is-locked');
    setTimeout(() => $('#SearchModalInput')?.focus(), 30);
  }

  function closeSearch() {
    const modal = searchModal();
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    if (!activeDrawer) {
      overlay()?.classList.remove('is-visible');
      document.body.classList.remove('is-locked');
    }
  }

  document.addEventListener('click', (e) => {
    if (e.target.closest('[data-open-search]')) {
      e.preventDefault();
      openSearch();
    }
    if (e.target.closest('[data-close-search]')) closeSearch();
  });

  function sizedImage(src, width) {
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (_) {
      return src;
    }
  }

  function initPredictiveSearch() {
    const input = $('[data-predictive-input]');
    const results = $('[data-predictive-results]');
    if (!input || !results) return;
    let timer;
    let controller;
    const escapeHtml = (s) => String(s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = input.value.trim();
      if (q.length < 2) {
        results.innerHTML = '';
        return;
      }
      timer = setTimeout(async () => {
        controller?.abort();
        controller = new AbortController();
        try {
          const url = `${theme.routes.root.replace(/\/$/, '')}/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=6&resources[options][fields]=title,product_type,variants.title,vendor`;
          const res = await fetch(url, { signal: controller.signal });
          const data = await res.json();
          const products = data?.resources?.results?.products || [];
          if (!products.length) {
            results.innerHTML = `<p class="text-muted">${escapeHtml(results.dataset.noResults || '—')}</p>`;
            return;
          }
          results.innerHTML = `<ul class="predictive" role="list">${products
            .map((p) => `
              <li><a class="predictive__item" href="${p.url}">
                ${p.image ? `<img src="${sizedImage(p.image, 120)}" alt="" loading="lazy" width="56" height="56">` : '<span style="width:56px;height:56px"></span>'}
                <span><span class="predictive__title">${escapeHtml(p.title)}</span><br>
                <span class="text-muted">${formatMoney(Math.round(parseFloat(p.price) * 100))}</span></span>
              </a></li>`)
            .join('')}</ul>
            <a class="link-arrow predictive__all" href="${theme.routes.root.replace(/\/$/, '')}/search?q=${encodeURIComponent(q)}&options[prefix]=last">→ ${escapeHtml(q)}</a>`;
        } catch (err) {
          if (err.name !== 'AbortError') results.innerHTML = '';
        }
      }, 220);
    });
  }

  /* ---------- Cart ---------- */
  function updateCartCount(count) {
    $$('[data-cart-count]').forEach((el) => {
      el.textContent = count;
      el.classList.toggle('is-empty', count === 0);
      el.classList.remove('is-bumped');
      void el.offsetWidth;
      el.classList.add('is-bumped');
    });
  }

  async function refreshCartDrawer() {
    const url = new URL(theme.routes.root, window.location.origin);
    url.searchParams.set('sections', 'cart-drawer');
    const res = await fetch(url.toString());
    const data = await res.json();
    const html = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
    const fresh = $('[data-cart-drawer]', html);
    const current = $('[data-cart-drawer]');
    if (fresh && current) {
      current.innerHTML = fresh.innerHTML;
    }
  }

  async function addToCart(form) {
    const button = $('[type="submit"]', form);
    const errorEl = $('[data-form-error]', form);
    if (errorEl) errorEl.hidden = true;
    button?.classList.add('is-loading');
    try {
      const formData = new FormData(form);
      // quantity input may live outside the form (form="" attribute)
      if (form.id) {
        $$(`[form="${form.id}"]`).forEach((input) => input.name && formData.set(input.name, input.value));
      }
      const res = await fetch(`${theme.routes.cartAdd}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.description || data.message || 'Error');
      const cart = await (await fetch(`${theme.routes.cart}.js`)).json();
      updateCartCount(cart.item_count);
      await refreshCartDrawer();
      openDrawer('CartDrawer');
    } catch (err) {
      if (errorEl) {
        errorEl.textContent = err.message;
        errorEl.hidden = false;
      } else {
        window.alert(err.message);
      }
    } finally {
      button?.classList.remove('is-loading');
    }
  }

  document.addEventListener('submit', (e) => {
    const form = e.target;
    const action = form.getAttribute('action') || '';
    if (theme.cartType !== 'drawer' || !document.getElementById('CartDrawer')) return;
    if (!/\/cart\/add/.test(action)) return;
    if (e.submitter && e.submitter.classList.contains('shopify-payment-button__button')) return;
    e.preventDefault();
    addToCart(form);
  });

  async function changeLine(line, quantity) {
    const drawer = $('[data-cart-drawer]');
    drawer?.classList.add('is-updating');
    try {
      const res = await fetch(`${theme.routes.cartChange}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line: Number(line), quantity: Number(quantity) }),
      });
      const cart = await res.json();
      if (!res.ok) throw new Error(cart.description || 'Error');
      updateCartCount(cart.item_count);
      await refreshCartDrawer();
    } catch (err) {
      window.alert(err.message);
      await refreshCartDrawer();
    } finally {
      $('[data-cart-drawer]')?.classList.remove('is-updating');
    }
  }

  // Quantity +/- buttons (drawer, cart page, product page)
  document.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-qty-change]');
    if (btn) {
      const wrap = btn.closest('[data-qty]');
      const input = $('[data-qty-input]', wrap);
      const min = Number(input.min || 0);
      const next = Math.max(min, Number(input.value || 0) + Number(btn.dataset.qtyChange));
      input.value = next;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }
    const remove = e.target.closest('[data-cart-drawer] [data-remove]');
    if (remove) {
      changeLine(remove.closest('[data-line]').dataset.line, 0);
    }
  });

  let qtyTimer;
  document.addEventListener('change', (e) => {
    const input = e.target;

    // Cart drawer quantity
    if (input.matches('[data-cart-drawer] [data-qty-input]')) {
      clearTimeout(qtyTimer);
      qtyTimer = setTimeout(() => changeLine(input.closest('[data-line]').dataset.line, input.value), 350);
      return;
    }

    // Auto-submitting selects / cart page inputs
    if (input.matches('[data-autosubmit]')) {
      clearTimeout(qtyTimer);
      qtyTimer = setTimeout(() => {
        const form = input.form;
        if (!form) return;
        if (form.matches('[data-filter-form]')) submitFilterForm(form);
        else form.submit();
      }, input.tagName === 'SELECT' ? 0 : 500);
      return;
    }

    // Filter checkboxes apply instantly on desktop
    if (input.closest('[data-filter-form]') && input.type === 'checkbox' && window.matchMedia('(min-width: 990px)').matches) {
      submitFilterForm(input.form);
    }
  });

  /* ---------- Collection filters ---------- */
  function submitFilterForm(form) {
    const params = new URLSearchParams();
    new FormData(form).forEach((value, key) => {
      if (String(value).trim() !== '') params.append(key, value);
    });
    window.location.search = params.toString();
  }

  document.addEventListener('submit', (e) => {
    if (e.target.matches('[data-filter-form]')) {
      e.preventDefault();
      submitFilterForm(e.target);
    }
  });

  /* ---------- Product page ---------- */
  function initProduct(section) {
    if (!section || section.dataset.ready) return;
    section.dataset.ready = '1';
    const sectionId = section.dataset.sectionId;
    const picker = $('[data-variant-picker]', section);
    const variantsEl = $('[data-variants]', section);
    const variants = variantsEl ? JSON.parse(variantsEl.textContent) : [];

    initGallery(section);

    if (!picker) return;
    picker.addEventListener('change', async () => {
      const selected = $$('fieldset', picker).map((fs) => $('input:checked', fs)?.value);
      $$('[data-selected-value]', picker).forEach((el, i) => (el.textContent = selected[i] || ''));
      const variant = variants.find((v) => v.options.every((opt, i) => opt === selected[i]));
      const button = $('[data-buy-buttons] [type="submit"]', section);
      if (!variant) {
        if (button) button.disabled = true;
        return;
      }

      $$('[data-variant-id]', section).forEach((input) => (input.value = variant.id));
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());

      if (variant.featured_media) scrollToMedia(section, variant.featured_media.id);

      // Re-render price, button state & stock from the server for exact formatting
      try {
        const res = await fetch(`${section.dataset.url}?variant=${variant.id}&section_id=${sectionId}`);
        const html = new DOMParser().parseFromString(await res.text(), 'text/html');
        const swap = (sel) => {
          const fresh = $(sel, html);
          const current = $(sel, section);
          if (fresh && current) current.innerHTML = fresh.innerHTML;
        };
        swap('[data-product-price]');
        const freshBtn = $('[data-buy-buttons] [type="submit"]', html);
        if (freshBtn && button) {
          button.disabled = freshBtn.disabled;
          button.textContent = freshBtn.textContent;
        }
        const freshStock = $('[data-stock]', html);
        const currentStock = $('[data-stock]', section);
        if (currentStock && freshStock) currentStock.replaceWith(freshStock);
        else if (currentStock) currentStock.remove();
        else if (freshStock) $('.product__buy', section)?.append(freshStock);
      } catch (_) {
        if (button) button.disabled = !variant.available;
      }
    });
  }

  function scrollToMedia(root, mediaId) {
    const slides = $('[data-gallery-slides]', root);
    const slide = $(`[data-media-id="${mediaId}"]`, root);
    if (slides && slide) slides.scrollTo({ left: slide.offsetLeft, behavior: 'smooth' });
  }

  function initGallery(root) {
    const slides = $('[data-gallery-slides]', root);
    if (!slides) return;
    const thumbs = $$('[data-thumb]', root);
    const step = (dir) => slides.scrollBy({ left: dir * slides.clientWidth, behavior: 'smooth' });
    $('[data-gallery-prev]', root)?.addEventListener('click', () => step(-1));
    $('[data-gallery-next]', root)?.addEventListener('click', () => step(1));
    thumbs.forEach((t) => t.addEventListener('click', () => scrollToMedia(root, t.dataset.thumb)));

    let raf;
    slides.addEventListener('scroll', () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const index = Math.round(slides.scrollLeft / slides.clientWidth);
        thumbs.forEach((t, i) => t.classList.toggle('is-active', i === index));
        thumbs[index]?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    }, { passive: true });
  }

  /* ---------- Product sliders ---------- */
  function initSliders(root = document) {
    $$('[data-slider]', root).forEach((slider) => {
      const section = slider.closest('section');
      $$('[data-slide]', section).forEach((btn) => {
        if (btn.dataset.ready) return;
        btn.dataset.ready = '1';
        btn.addEventListener('click', () => {
          const item = slider.firstElementChild;
          const amount = item ? item.getBoundingClientRect().width + 20 : slider.clientWidth;
          slider.scrollBy({ left: btn.dataset.slide === 'next' ? amount : -amount, behavior: 'smooth' });
        });
      });
    });
  }

  /* ---------- Recommendations ---------- */
  function initRecommendations(root = document) {
    $$('[data-recommendations]', root).forEach(async (el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = '1';
      try {
        const res = await fetch(el.dataset.url);
        const html = new DOMParser().parseFromString(await res.text(), 'text/html');
        const fresh = $('[data-recommendations]', html);
        if (fresh && fresh.innerHTML.trim()) {
          el.innerHTML = fresh.innerHTML;
          initReveal(el);
        }
      } catch (_) { /* ignore */ }
    });
  }

  /* ---------- Reveal on scroll ---------- */
  let observer;
  function initReveal(root = document) {
    const items = $$('[data-reveal]:not(.is-revealed)', root);
    if (!('IntersectionObserver' in window) || document.documentElement.classList.contains('no-animations')) {
      items.forEach((el) => el.classList.add('is-revealed'));
      return;
    }
    observer = observer || new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const siblings = el.parentElement ? Array.from(el.parentElement.children) : [];
        const delay = Math.min(siblings.indexOf(el), 6) * 70;
        el.style.transitionDelay = `${delay}ms`;
        el.classList.add('is-revealed');
        observer.unobserve(el);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });
    items.forEach((el) => observer.observe(el));
  }

  /* ---------- Vehicle finder ---------- */
  const VEHICLE_KEY = 'cargear:vehicle';

  function getVehicle() {
    try {
      const v = JSON.parse(localStorage.getItem(VEHICLE_KEY));
      return v && v.make ? v : null;
    } catch (_) {
      return null;
    }
  }

  function setVehicle(v) {
    try {
      if (v) localStorage.setItem(VEHICLE_KEY, JSON.stringify(v));
      else localStorage.removeItem(VEHICLE_KEY);
    } catch (_) { /* storage blocked */ }
    document.dispatchEvent(new CustomEvent('vehicle:change', { detail: v }));
  }

  const vehicleName = (v) => (v ? `${v.make}${v.model ? ` ${v.model}` : ''}` : '');

  function parseVehicles(text) {
    return String(text || '')
      .split(/\r?\n/)
      .map((line) => {
        const idx = line.indexOf(':');
        if (idx === -1) return line.trim() ? { make: line.trim(), models: [] } : null;
        return {
          make: line.slice(0, idx).trim(),
          models: line.slice(idx + 1).split(',').map((m) => m.trim()).filter(Boolean),
        };
      })
      .filter((v) => v && v.make);
  }

  function vehicleUrl(finder, v) {
    const data = parseVehicles(finder.dataset.vehicles);
    const entry = data.find((d) => d.make === v.make);
    const values = v.model ? [`${v.make} ${v.model}`] : (entry?.models.length ? entry.models.map((m) => `${v.make} ${m}`) : [v.make]);
    if (finder.dataset.universal) values.push(finder.dataset.universal);
    const url = new URL(finder.dataset.target, window.location.origin);
    values.forEach((value) => url.searchParams.append(finder.dataset.param, value));
    return url.toString();
  }

  function initVehicleFinder(finder) {
    if (finder.dataset.ready) return;
    finder.dataset.ready = '1';
    const data = parseVehicles(finder.dataset.vehicles);
    const makeSel = $('[data-vf-make]', finder);
    const modelSel = $('[data-vf-model]', finder);
    const submit = $('[data-vf-submit]', finder);
    const allModelsLabel = modelSel.options[0]?.textContent || '';

    data.forEach((d) => makeSel.add(new Option(d.make, d.make)));

    const fillModels = (make, selected = '') => {
      const entry = data.find((d) => d.make === make);
      modelSel.length = 0;
      modelSel.add(new Option(allModelsLabel, ''));
      (entry?.models || []).forEach((m) => modelSel.add(new Option(m, m, false, m === selected)));
      modelSel.disabled = !entry || !entry.models.length;
      submit.disabled = !make;
    };

    const render = () => {
      const v = getVehicle();
      const current = $('[data-vf-current]', finder);
      if (v) {
        makeSel.value = v.make;
        fillModels(v.make, v.model);
        $('[data-vf-current-name]', finder).textContent = vehicleName(v);
        current.hidden = false;
      } else {
        current.hidden = true;
      }
    };

    makeSel.addEventListener('change', () => fillModels(makeSel.value));
    submit.addEventListener('click', () => {
      if (!makeSel.value) return;
      const v = { make: makeSel.value, model: modelSel.value };
      setVehicle(v);
      if (finder.dataset.mode !== 'save') window.location.href = vehicleUrl(finder, v);
    });
    $('[data-vf-clear]', finder).addEventListener('click', () => {
      setVehicle(null);
      makeSel.value = '';
      fillModels('');
    });
    document.addEventListener('vehicle:change', render);
    render();
  }

  // Brand quick-select chips
  document.addEventListener('click', (e) => {
    const chip = e.target.closest('[data-vf-brand]');
    if (!chip) return;
    const finder = $('[data-vehicle-finder]', chip.closest('section')) || $('[data-vehicle-finder]');
    if (!finder) return;
    const v = { make: chip.dataset.vfBrand, model: '' };
    setVehicle(v);
    window.location.href = vehicleUrl(finder, v);
  });

  function initFitment(root = document) {
    $$('[data-fitment]', root).forEach((el) => {
      if (el.dataset.ready) return;
      el.dataset.ready = '1';
      let values = [];
      try {
        values = JSON.parse(el.dataset.values || 'null') || [];
      } catch (_) { /* ignore */ }
      if (!Array.isArray(values)) values = [values];
      const norm = (s) => String(s).toLowerCase().replace(/\s+/g, ' ').trim();
      const list = values.map(norm);
      const universal = norm(el.dataset.universal || '');
      const status = $('[data-fitment-status] span', el);
      const finderWrap = $('[data-fitment-finder]', el);
      const initial = status.textContent;

      const update = () => {
        const v = getVehicle();
        el.classList.remove('fitment--fits', 'fitment--nofit');
        if (universal && list.includes(universal)) {
          el.classList.add('fitment--fits');
          status.textContent = el.dataset.textUniversal;
          finderWrap.hidden = true;
          return;
        }
        if (!v) {
          status.textContent = initial;
          finderWrap.hidden = false;
          return;
        }
        const name = vehicleName(v);
        const fits = v.model
          ? list.includes(norm(name))
          : list.some((x) => x.startsWith(`${norm(v.make)} `) || x === norm(v.make));
        el.classList.add(fits ? 'fitment--fits' : 'fitment--nofit');
        status.textContent = (fits ? el.dataset.textFits : el.dataset.textNofit).replace('__V__', name);
        finderWrap.hidden = false;
      };
      document.addEventListener('vehicle:change', update);
      update();
    });
  }

  /* ---------- Init ---------- */
  function init(root = document) {
    initAnnouncements();
    initSliders(root);
    initRecommendations(root);
    initReveal(root);
    $$('[data-product-section]', root).forEach(initProduct);
    $$('[data-vehicle-finder]', root).forEach(initVehicleFinder);
    initFitment(root);
  }

  document.addEventListener('DOMContentLoaded', () => {
    initHeader();
    initPredictiveSearch();
    init();
  });

  // Theme editor support
  document.addEventListener('shopify:section:load', (e) => {
    init(e.target);
    if (e.target.querySelector('[data-header]')) initHeader();
  });
  document.addEventListener('shopify:section:select', (e) => {
    if (e.target.querySelector('#CartDrawer')) openDrawer('CartDrawer');
  });
})();
